import { useState } from "react";
import { UserPlus, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { TimingFields } from "@/components/quote/TimingFields";
import { LocationSearch } from "@/components/quote/LocationSearch";
import { autoSchedule } from "@/lib/schedule";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="micro-label">{label}</Label>
    {children}
  </div>
);

const ClientPicker = ({ q, clients, onPickClient, onAddClient }) => {
  const [open, setOpen] = useState(false);
  const unsaved = !q.client_id && q.client?.name;
  return (
    <Field label="Cliente" className="sm:col-span-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between font-normal" data-testid="quote-client-select">
            <span className="truncate">{q.client?.name || "Seleziona cliente"}</span>
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[min(460px,90vw)]" align="start">
          <Command filter={(value, s) => (value.toLowerCase().includes(s.toLowerCase()) ? 1 : 0)}>
            <CommandInput placeholder="Cerca per nome, P.IVA o città..." data-testid="client-picker-search" />
            <CommandList className="max-h-72">
              <CommandEmpty>Nessun cliente.</CommandEmpty>
              {clients.map((c) => (
                <CommandItem key={c.id} value={`${c.name} ${c.piva || ""} ${c.city || ""} ${c.id}`} onSelect={() => { onPickClient(c); setOpen(false); }} data-testid={`client-pick-${c.id}`}>
                  <div className="min-w-0"><div className="truncate">{c.name}</div><div className="text-xs text-slate-500 truncate">{[c.city, c.piva].filter(Boolean).join(" · ")}</div></div>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {unsaved && (
        <div className="flex items-center gap-2 text-xs text-amber-300 mt-1" data-testid="client-unsaved-hint">
          "{q.client.name}" non è in archivio.
          <Button size="sm" variant="link" className="h-auto p-0 text-amber-400" onClick={() => onAddClient({ name: q.client.name })} data-testid="client-save-archive-btn">
            <UserPlus className="h-3 w-3 mr-1" />Salva in archivio
          </Button>
        </div>
      )}
    </Field>
  );
};

const setRange = (q, update, patch) => {
  const next = { ...q, ...patch };
  if (next.event_date_end && next.event_date && next.event_date_end < next.event_date) next.event_date_end = "";
  update({ event_date: next.event_date, event_date_end: next.event_date_end, ...autoSchedule(next) });
};

export const HeaderForm = ({ q, update, clients, onPickClient, onAddClient }) => (
  <section className="bg-slate-900 border border-slate-800 rounded-lg p-4 sm:p-5" data-testid="quote-header-form">
    <h2 className="text-base md:text-lg font-semibold mb-4">Dati evento</h2>
    <div className="grid sm:grid-cols-4 gap-4">
      <ClientPicker q={q} clients={clients} onPickClient={onPickClient} onAddClient={onAddClient} />
      <Field label="Data evento dal">
        <Input type="date" min="2000-01-01" max="2099-12-31" value={q.event_date || ""} onChange={(e) => setRange(q, update, { event_date: e.target.value })} data-testid="quote-event-date-input" />
      </Field>
      <Field label="Data evento al">
        <Input type="date" min={q.event_date || "2000-01-01"} max="2099-12-31" value={q.event_date_end || ""} onChange={(e) => setRange(q, update, { event_date_end: e.target.value })} data-testid="quote-event-date-end-input" />
      </Field>
      <Field label={q.event_date ? "Giorni di evento (calcolati dalle date)" : "Giorni di evento"} className="sm:col-span-2">
        <Input type="number" min={1} value={q.days} readOnly={!!q.event_date} onChange={(e) => update({ days: Math.max(1, parseInt(e.target.value || "1", 10)) })} className={`font-mono ${q.event_date ? "opacity-70 cursor-not-allowed" : ""}`} data-testid="quote-days-input" />
      </Field>
      <Field label="Oggetto" className="sm:col-span-4">
        <Input value={q.subject} onChange={(e) => update({ subject: e.target.value })} placeholder="Servizi Tecnici per evento ..." data-testid="quote-subject-input" />
      </Field>
      <Field label="Luogo" className="sm:col-span-4">
        <LocationSearch q={q} update={update} />
      </Field>
      <TimingFields q={q} update={update} />
      {q.timing && (
        <Field label="Note timing (vecchio testo)" className="sm:col-span-4">
          <Textarea rows={2} value={q.timing} onChange={(e) => update({ timing: e.target.value })} className="resize-y" data-testid="quote-timing-input" />
        </Field>
      )}
    </div>
  </section>
);
